import jsPDF from "jspdf";
import JsBarcode from "jsbarcode";

const MAX_RECEIPT_HEIGHT = 203.2; // 8 inches (mm)

/* ---------------- HELPERS ---------------- */

const loadImageAsBase64 = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.src = url;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = reject;
  });

const generateBarcode = (value) =>
  new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement("canvas");
      JsBarcode(canvas, value, {
        format: "CODE128",
        width: 2,
        height: 50,
        displayValue: false,
        margin: 0,
      });
      resolve(canvas.toDataURL("image/png"));
    } catch (err) {
      reject(err);
    }
  });

  const formatLocalDate = (dateString) => {
  if (!dateString) return "-";

  const [year, month, day] = dateString.split("-");

  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day)
  ).toLocaleDateString();
};


/* ---------------- MAIN ---------------- */

export const printReceipt =
  async (
    sale,
    mode = "print"
  ) => {
    
  /* -------- HEIGHT CALCULATION -------- */

  const itemCount = Array.isArray(sale.items) ? sale.items.length : 0;

  const hasMMG =
    sale.receiver_info &&
    (sale.receiver_info.name ||
      sale.receiver_info.trans_id ||
      sale.receiver_info.acc_number);

  const hasFerry = !!sale.ferry_info;

  const hasReceiptBarcode = !!sale.receipt_no;
  
const calculatedHeight =
  110 +                         // header
  itemCount * 6 +               // items
  40 +                          // totals
  (sale.amount_tendered ? 15 : 0) +
  25 +                          // footer
  (hasMMG ? 45 : 0) +
  (hasFerry ? 90 : 0) +
  (hasReceiptBarcode ? 28 : 0); // ✅ RECEIPT BARCODE SPACE


  const needsCompression = calculatedHeight > MAX_RECEIPT_HEIGHT;
 const totalHeight = calculatedHeight;

  /* -------- COMPRESSION RULES -------- */

  const lineGap = needsCompression ? 4 : 6;
  const barcodeHeight = needsCompression ? 14 : 18;
  const bodyFont = needsCompression ? 7 : 8;
  const headerFont = needsCompression ? 9 : 10;
  const totalFont = needsCompression ? 10 : 12;

  /* -------- CREATE PDF -------- */

  const doc = new jsPDF({
    unit: "mm",
    format: [80, totalHeight],
    orientation: "portrait",
  });

  let y = 8;

  /* -------- LOGO -------- */

  const logo = await loadImageAsBase64(require("../assets/logo.png"));
  doc.addImage(logo, "PNG", 25, y, 30, 30);
  y += 32;

  /* -------- HEADER -------- */

  doc.setFontSize(headerFont).setFont(undefined, "bold");
  doc.text("RCS TOPUP & BILLING ENTITY", 40, y, { align: "center" });
  y += lineGap;

  doc.setFont(undefined, "normal");
  doc.text("22 BELLE PLAINE WAKENAAM", 40, y, { align: "center" });
  y += lineGap;
  doc.text("(592) 662-7987; rcs@rcsgy.com; www.rcsgy.com", 40, y, { align: "center" });
  y += lineGap;

  doc.setFontSize(bodyFont);
  [
    "MMG+ Distributor, Digicel Distributor,",
    "Enet & Gtt Distributor, Top-up & Phone Cards,",
    "Massy Agent & Bill Payments,",
    "Printery and Insurance Broker.",
    "Micro loans and pawn brokerage",
  ].forEach((l) => {
    doc.text(l, 40, y, { align: "center" });
    y += lineGap - 1;
  });

  doc.line(5, (y += 2), 75, y);

  doc.setFont(undefined, "bold");
  doc.text(`Receipt #: ${sale.receipt_no}`, 40, (y += lineGap), {
    align: "center",
  });

  /* -------- TRANSACTION DETAILS -------- */

  doc.setFontSize(bodyFont).setFont(undefined, "bold");
  doc.text("Transaction Details:", 40, (y += lineGap), { align: "center" });
  doc.line(5, (y += 2), 75, y);

  doc.setFont(undefined, "normal");
  doc.text(`Date: ${new Date(sale.created_at).toLocaleString()}`, 5, (y += lineGap));
  doc.text(`Type: ${sale.payment_method || "Cash"}`, 5, (y += lineGap));
  doc.text(`Sender: ${sale.customer?.name || sale.customer_name || "Unknown"}`, 5, (y += lineGap));
  doc.text(`Phone No.: ${sale.customer?.phone || "-"}`, 5, (y += lineGap));
  doc.text(`Address: ${sale.customer?.address || "-"}`, 5, (y += lineGap));

  doc.line(5, (y += 3), 75, y);

  /* -------- ITEMS -------- */

  doc.setFont(undefined, "bold");
  doc.text("Items:", 5, (y += lineGap));

  doc.setFont(undefined, "normal");
  let items = sale.items;
  if (typeof items === "string") {
    try {
      items = JSON.parse(items);
    } catch {
      items = [];
    }
  }

  items.forEach((it, i) => {
    doc.text(
      `${i + 1}. ${it.product || it.name} x${it.quantity || 1} @ $${Number(
        it.price || 0
      ).toFixed(2)}`,
      5,
      (y += lineGap)
    );
  });

  doc.line(5, (y += 3), 75, y);

  /* -------- MMG DETAILS -------- */

if (hasMMG) {
  const receiver = sale.receiver_info || {};

  const mmgIcon = await loadImageAsBase64(require("../assets/mmg.png"));
  doc.addImage(mmgIcon, "PNG", 32, (y += 2), 16, 10);
  y += 10;

  doc.setFont(undefined, "bold");
  doc.text("MMG TRANSACTION", 40, (y += lineGap), { align: "center" });

  doc.line(5, (y += 2), 75, y);

  // 🟢 SENDER
  const senderIcon = await loadImageAsBase64(require("../assets/sender.png"));
  doc.addImage(senderIcon, "PNG", 5, (y += 3), 5, 5);

  doc.setFont(undefined, "normal");
  doc.text(
    `Sender: ${sale.customer?.name || sale.customer_name || "Unknown"}`,
    12,
    y + 4
  );

  // 🟢 RECEIVER
  const receiverIcon = await loadImageAsBase64(require("../assets/receiver.png"));
  doc.addImage(receiverIcon, "PNG", 5, (y += 6), 5, 5);

  doc.text(
    `Receiver: ${receiver.name || "-"}`,
    12,
    y + 4
  );

  y += 6;

  if (receiver.trans_id) {
    doc.text(`MMG Trans ID: ${receiver.trans_id}`, 5, (y += lineGap));
  }

  if (receiver.acc_number) {
    doc.text(`Account No.: ${receiver.acc_number}`, 5, (y += lineGap));
  }

  doc.line(5, (y += 3), 75, y);
}

  /* -------- FERRY DETAILS -------- */

if (hasFerry) {
  const ferry = typeof sale.ferry_info === "string"
    ? JSON.parse(sale.ferry_info)
    : sale.ferry_info;

  doc.line(5, (y += 3), 75, y);

  // TITLE
  doc.setFont(undefined, "bold").setFontSize(headerFont);
  doc.text("FERRY PASS", 40, (y += lineGap), { align: "center" });

const ferryIcon = await loadImageAsBase64(require("../assets/ferry.png"));
doc.addImage(ferryIcon, "PNG", 30, (y += 2), 20, 10);
y += 12;

  doc.line(5, (y += 2), 75, y);

  // MAIN INFO (BIG + CENTERED)
  doc.setFont(undefined, "bold").setFontSize(totalFont);

doc.setFont(undefined, "bold").setFontSize(bodyFont + 1);

doc.text(`Vehicle Number: ${ferry.vehicle_no || "-"}`, 40, (y += lineGap + 2), { align: "center" });
doc.text(`Booking Number: ${ferry.booking || "-"}`, 40, (y += lineGap), { align: "center" });
doc.text(`Payment Number: ${ferry.payment || "-"}`, 40, (y += lineGap), { align: "center" });

  doc.line(5, (y += 3), 75, y);

  // DETAILS
  doc.setFont(undefined, "bold");
  doc.text("Trip Details", 40, (y += lineGap), { align: "center" });

  doc.setFont(undefined, "normal");

  const routeFormatted = ferry.route
    ? ferry.route.replace("-", " -> ")
    : "-";

const travelDate = formatLocalDate(ferry.travel_date);

const returnText = ferry.round_trip
    ? ferry.return_date
      ? formatLocalDate(ferry.return_date)
      : "ROUND TRIP"
    : "ONE WAY";

  doc.text(`Type: ${ferry.vehicle_type || "-"}`, 40, (y += lineGap), { align: "center" });
  doc.text(`Date: ${travelDate}`, 40, (y += lineGap), { align: "center" });
  doc.text(`Route: ${routeFormatted}`, 40, (y += lineGap), { align: "center" });
  doc.text(`Trip: ${returnText}`, 40, (y += lineGap), { align: "center" });

  // BARCODE
  const barcode = await generateBarcode(ferry.booking || "FERRY");
  doc.addImage(barcode, "PNG", 10, (y += 5), 60, barcodeHeight);

  y += barcodeHeight + 2;
}

  /* -------- TOTALS -------- */

  doc.line(5, (y += 2), 75, y);

  doc.text(`Subtotal: $${Number(sale.subtotal || 0).toFixed(2)}`, 5, (y += lineGap));
  doc.text(`Discounts: -$${Number(sale.discount || 0).toFixed(2)}`, 5, (y += lineGap));

  if (sale.points_redeemed)
    doc.text(
      `Points Redeemed: -$${Number(sale.points_redeemed * 2).toFixed(2)}`,
      5,
      (y += lineGap)
    );

  doc.text(`Taxes: $${Number(sale.tax || 0).toFixed(2)}`, 5, (y += lineGap));

// 🧾 TOTAL BOX
const totalText = `TOTAL: $${Number(sale.total ?? 0).toFixed(2)}`;

// Box dimensions
const boxWidth = 70;
const boxHeight = 10;
const boxX = 5;
const boxY = y + 3;

// Draw rectangle
doc.setDrawColor(0);
doc.setLineWidth(0.5);
doc.rect(boxX, boxY, boxWidth, boxHeight);

// Center text inside box
doc.setFont(undefined, "bold").setFontSize(totalFont + 1);
doc.text(totalText, 40, boxY + 6.5, { align: "center" });

y = boxY + boxHeight + 2;

  /* -------- FOOTER -------- */

  doc.setFontSize(bodyFont).setFont(undefined, "normal");
  doc.text("Thank you for your business! Check out our website @www.rcsgy.com", 40, (y += lineGap + 5), {
    align: "center",
  });

  /* -------- RECEIPT BARCODE -------- */

  if (sale.receipt_no) {
    const receiptBarcode = await generateBarcode(sale.receipt_no);
    doc.addImage(receiptBarcode, "PNG", 10, (y += 4), 60, barcodeHeight);
  }

const pdfBlob =
  doc.output("blob");

if (mode === "print") {

  doc.autoPrint();

  window.open(
    doc.output("bloburl"),
    "_blank"
  );
}

return pdfBlob;
};
